import { doc, onSnapshot, collection, query } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Listen to a single Firestore document in real-time
 * @param {string} collectionName - The collection name
 * @param {string} docId - The document ID
 * @param {function} callback - Called with document data on changes
 * @param {function} onError - Called on errors
 * @returns {function} Unsubscribe function
 */
export const listenToDocument = (collectionName, docId, callback, onError) => {
  if (!docId) {
    console.warn(`listenToDocument called with empty docId for ${collectionName}`);
    return () => {};
  }
  
  try {
    const docRef = doc(db, collectionName, docId);
    const unsubscribe = onSnapshot(
      docRef, 
      {
        // Include metadata changes to detect pending writes
        includeMetadataChanges: false
      },
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          callback({ id: docSnapshot.id, ...docSnapshot.data() });
        } else {
          callback(null);
        }
      }, 
      (error) => {
        console.error(`Error listening to ${collectionName}/${docId}:`, error);
        if (onError) onError(error);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error(`Failed to setup listener for ${collectionName}/${docId}:`, error);
    if (onError) onError(error);
    return () => {};
  }
};

/**
 * Listen to a Firestore collection query in real-time
 * @param {string} collectionName - The collection name
 * @param {array} queryConstraints - Array of Firestore query constraints
 * @param {function} callback - Called with array of documents on changes
 * @param {function} onError - Called on errors
 * @returns {function} Unsubscribe function
 */
export const listenToCollection = (collectionName, queryConstraints = [], callback, onError) => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = query(collectionRef, ...queryConstraints);

    const unsubscribe = onSnapshot(
      q,
      {
        // Don't include metadata changes to reduce listener churn
        includeMetadataChanges: false
      },
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        callback(data);
      }, 
      (error) => {
        console.error(`Error listening to ${collectionName}:`, error);
        if (onError) onError(error);
      }
    );
    
    return unsubscribe;
  } catch (error) {
    console.error(`Failed to setup collection listener for ${collectionName}:`, error);
    if (onError) onError(error);
    return () => {};
  }
};

